import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { TRANSFER_KIND_ORDER, TX_TYPE_LABELS, familyOf, kindAllows, srcAllows, subsFor, transferSubs, type TransferKind, type Account, type AccountSubtype, type Category, type Transaction, type TxType } from "@/lib/finance";
import { todayISO } from "@/lib/format";
import { TYPE_OPTS, isExpenseType, typeFor, type BudgetItemRow } from "@/lib/budget";
import { MoneyInput, formatMoneyInput, parseMoney } from "@/components/MoneyInput";
import { DateInput } from "@/components/DateInput";
import { ConceptInput, matchItem } from "@/components/movements/ConceptInput";
import { FxFields, type FxState } from "@/components/movements/FxFields";
import { NEW_ACCOUNT, useInlineAccount } from "@/components/accounts/useInlineAccount";

const GROUP_OPTS = TYPE_OPTS.map((t) => ({ value: t.value as string, label: t.label }));

const NONE = "none";
const BASE_TYPES: TxType[] = ["income", "expense", "transfer"];

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  accounts: Account[];
  categories: Category[];
  items: BudgetItemRow[];
  initial: Transaction | null;
  onSaved: () => void;
}

const blank = (accounts: Account[]) => ({
  type: "expense" as TxType, amount: "", sign: "+", description: "", date: todayISO(), notes: "",
  source: accounts.find((a) => a.account_type === "debit")?.id ?? accounts[0]?.id ?? "", destination: "", category: NONE, item: NONE, group: NONE,
  kind: "savings" as TransferKind, msi: NONE, fx: "", dest: "", orig: "", origCur: "USD",
});
const MSI_OPTS = [{ value: NONE, label: "No" }, ...[3, 6, 9, 12, 18, 24].map((n) => ({ value: String(n), label: `${n} meses` }))];
const nativeOf = (t: Transaction) => (t.type === "expense" || t.type === "transfer" ? t.source_native : t.dest_native) ?? t.amount;

export function Pick({ label, value, onChange, options, testId, placeholder, onCreate }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; testId: string; placeholder: string; onCreate?: () => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <Select value={value} onValueChange={(v: string) => { if (!v) return; if (v === NEW_ACCOUNT) onCreate?.(); else onChange(v); }}>
        <SelectTrigger className="h-11" data-testid={testId}><SelectValue placeholder={onCreate && !options.length ? "Crea tu cuenta" : placeholder} /></SelectTrigger>
        <SelectContent className="max-h-72 bg-white">
          {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}
          {onCreate && <SelectItem value={NEW_ACCOUNT} className="font-semibold text-[#2C4F91]" data-testid={`${testId}-create`}>+ Crear cuenta</SelectItem>}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TransactionFormDialog({ open, onOpenChange, accounts: accountsProp, categories, items, initial, onSaved }: Props) {
  const [f, setF] = useState(blank(accountsProp));
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const { all: accounts, create, dialog } = useInlineAccount(accountsProp);

  useEffect(() => {
    if (!open) return;
    setF(initial ? {
      type: initial.type, amount: formatMoneyInput(String(Math.abs(nativeOf(initial)))), sign: nativeOf(initial) < 0 ? "-" : "+",
      description: initial.description, date: initial.date, notes: initial.notes ?? "",
      source: initial.source_account_id ?? "", destination: initial.destination_account_id ?? "", category: initial.category_id ?? NONE, item: initial.budget_item_id ?? NONE, group: initial.group ?? NONE,
      kind: initial.transfer_kind ?? "savings", msi: initial.msi_total ? String(initial.msi_total) : NONE,
      fx: initial.fx_rate ? String(initial.fx_rate) : "",
      dest: initial.type === "transfer" && initial.dest_native != null ? formatMoneyInput(String(initial.dest_native)) : initial.type === "transfer" && initial.source_native != null ? formatMoneyInput(String(initial.amount)) : "",
      orig: initial.original_amount ? formatMoneyInput(String(initial.original_amount)) : "", origCur: initial.original_currency ?? "USD",
    } : blank(accountsProp));
  }, [open, initial, accountsProp]);

  const usable = accounts.filter((a) => !a.is_archived || a.id === f.source || a.id === f.destination);
  const accOptions = usable.map((a) => ({ value: a.id, label: a.name }));
  const incomeOptions = usable.filter((a) => familyOf(a.account_type) !== "credit").map((a) => ({ value: a.id, label: a.name }));
  const expenseSrcOptions = usable.filter((a) => familyOf(a.account_type) !== "investment").map((a) => ({ value: a.id, label: a.name }));
  const needsSource = f.type === "expense" || f.type === "transfer";
  const needsDest = f.type !== "expense";
  const destAcc = accounts.find((a) => a.id === f.destination);
  const srcAcc = accounts.find((a) => a.id === f.source);
  const isTransfer = f.type === "transfer";
  const transferDest = usable.filter((a) => a.id !== f.source && kindAllows(f.kind, a.account_type) && !(f.kind === "between" && srcAcc?.account_type === "cash" && a.account_type === "cash")).map((a) => ({ value: a.id, label: a.name }));
  const transferSrc = usable.filter((a) => srcAllows(f.kind, a.account_type)).map((a) => ({ value: a.id, label: a.name }));
  const setDest = (v: string) => setF((s) => (s.type === "transfer" ? { ...s, destination: v, category: NONE, item: NONE } : { ...s, destination: v }));
  const setKind = (k: TransferKind) => setF((s) => {
    const d = accounts.find((a) => a.id === s.destination);
    const o = accounts.find((a) => a.id === s.source);
    return { ...s, kind: k, category: NONE, item: NONE, destination: d && kindAllows(k, d.account_type) ? s.destination : "",
      source: o && srcAllows(k, o.account_type) ? s.source : usable.find((a) => srcAllows(k, a.account_type))?.id ?? "" };
  });
  const savingsTransfer = isTransfer && f.kind === "savings";
  const neutralTransfer = isTransfer && (f.kind === "between" || f.kind === "withdrawal" || (f.kind === "credit" && destAcc?.account_type !== "loan"));
  const msiAllowed = f.type === "expense" && srcAcc?.account_type === "credit_card";
  const loanPayment = isTransfer && f.kind === "credit" && destAcc?.account_type === "loan";
  const cardPayment = isTransfer && destAcc?.account_type === "credit_card";
  const investReturn = f.type === "adjustment" && destAcc?.account_type === "investment";
  const catType = loanPayment ? "expense" : f.type;
  const categoryRequired = f.type === "expense" || loanPayment;
  const showCategory = f.type !== "adjustment" && !cardPayment && !neutralTransfer;
  const showCatPicker = showCategory && f.type !== "income" && !savingsTransfer;
  const catOptions = categories.filter((c) => c.type === catType).map((c) => ({ value: c.id, label: c.name }));
  const category = catOptions.some((o) => o.value === f.category) ? f.category : NONE;
  const itemKind = f.type === "income" ? "income" : f.type === "expense" || loanPayment ? "expense" : "savings";
  const itemOptions = items.filter((i) => i.kind === itemKind).map((i) => ({ value: i.id, label: `${i.name} · ${i.category_name}` }));
  const item = itemOptions.some((o) => o.value === f.item) ? f.item : NONE;
  const pickItem = (v: string) => setF((s) => { const it = items.find((i) => i.id === v); return { ...s, item: v, category: it?.category_id ?? s.category, group: it && isExpenseType(it.group) ? it.group : s.group }; });
  const types: TxType[] = initial?.type === "adjustment" ? [...BASE_TYPES, "adjustment"] : BASE_TYPES;
  const isExpense = f.type === "expense";
  const usesConcept = isExpense || f.type === "income";
  const expenseItems = items.filter((i) => i.kind === (isExpense ? "expense" : "income") && (!isExpense || f.category === NONE || i.category_id === f.category));
  const matched = usesConcept ? items.find((i) => i.id === item && i.kind === (isExpense ? "expense" : "income")) : undefined;
  const typeConcept = (v: string) => setF((s) => {
    const m = matchItem(expenseItems, v);
    return m ? { ...s, description: v, item: m.id, category: m.category_id, group: isExpenseType(m.group) ? m.group : s.group } : { ...s, description: v, item: NONE };
  });
  const pickConcept = (i: BudgetItemRow) => setF((s) => ({ ...s, description: i.name, item: i.id, category: i.category_id, group: isExpenseType(i.group) ? i.group : s.group }));
  const pickCategory = (v: string) => setF((s) => {
    const c = categories.find((x) => x.id === v);
    return { ...s, category: v, item: items.find((i) => i.id === s.item)?.category_id === v ? s.item : NONE, group: c ? typeFor(s.description, c) : s.group };
  });
  const group = isExpenseType(f.group) ? f.group : NONE;
  const srcCur = needsSource ? srcAcc?.currency ?? "MXN" : null;
  const dstCur = needsDest ? destAcc?.currency ?? "MXN" : null;
  const cross = isTransfer && !!srcAcc && !!destAcc && srcCur !== dstCur;
  const foreign = cross ? null : [srcCur, dstCur].find((c) => c && c !== "MXN") ?? null;
  const amountCur = (needsSource ? srcCur : dstCur) ?? "MXN";
  const origAllowed = isExpense && srcCur === "MXN";
  const srcSubs: AccountSubtype[] = isTransfer ? transferSubs(f.kind, 0) : subsFor(["debit", "cash", "credit_card"]);
  const destSubs: AccountSubtype[] = isTransfer ? transferSubs(f.kind, 1) : f.type === "income" ? subsFor(["debit", "cash", "savings", "investment"]) : subsFor(["debit", "cash", "savings", "investment", "credit_card", "loan"]);
  const newSource = () => create(srcSubs, (a) => setF((s) => ({ ...s, source: a.id, msi: NONE })));
  const newDest = () => create(destSubs, (a) => setDest(a.id));

  const changeType = (t: TxType) => setF((s) => {
    const next = { ...s, type: t, category: NONE, item: NONE, msi: NONE,
      destination: t === "expense" || (t === "income" && !incomeOptions.some((o) => o.value === s.destination)) ? "" : s.destination };
    if (t !== "transfer") return next;
    const o = accounts.find((a) => a.id === s.source);
    const d = accounts.find((a) => a.id === s.destination);
    return { ...next, description: "", source: o && srcAllows(s.kind, o.account_type) ? s.source : usable.find((a) => srcAllows(s.kind, a.account_type))?.id ?? "",
      destination: d && kindAllows(s.kind, d.account_type) ? s.destination : "" };
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseMoney(f.amount);
    if (Number.isNaN(parsed)) return toast.error("Indica el monto");
    const amount = f.type === "adjustment" && f.sign === "-" ? -parsed : parsed;
    if (f.type !== "adjustment" && amount <= 0) return toast.error("El monto debe ser mayor a cero");
    if (f.type === "adjustment" && amount === 0) return toast.error("El ajuste no puede ser cero");
    if (!f.date) return toast.error("Escribe la fecha como dd/mm/aaaa");
    if (f.type !== "adjustment" && f.type !== "transfer" && !f.description.trim()) return toast.error("Escribe una descripción");
    if (needsSource && !f.source) return toast.error("Elige la cuenta de origen");
    if (needsDest && !f.destination) return toast.error("Elige la cuenta destino");
    if (f.type === "transfer" && f.source === f.destination) return toast.error("Origen y destino no pueden ser la misma cuenta");
    if (categoryRequired && category === NONE) return toast.error(loanPayment ? "El pago del préstamo necesita una categoría de gasto" : "Elige una categoría");
    if (isExpense && group === NONE) return toast.error("Elige el tipo: Esencial, Comodidad o Deuda");
    if (f.type === "adjustment" && !investReturn && !f.notes.trim()) return toast.error("El motivo del ajuste es obligatorio");
    if (cross && !(parseMoney(f.dest) > 0)) return toast.error(`Escribe el monto que entra en ${dstCur}`);
    const fxRate = parseMoney(f.fx);
    const orig = parseMoney(f.orig);
    const payload = {
      type: f.type, amount, description: f.description.trim(), date: f.date, notes: f.notes.trim() || null,
      source_account_id: needsSource ? f.source : null, destination_account_id: needsDest ? f.destination : null,
      category_id: showCatPicker && category !== NONE ? category : null,
      budget_item_id: showCategory && item !== NONE ? item : null,
      group: isExpense ? group : null,
      transfer_kind: isTransfer ? f.kind : null,
      msi_months: msiAllowed && f.msi !== NONE ? Number(f.msi) : null,
      fx_rate: (foreign || cross) && fxRate > 0 ? fxRate : null,
      destination_amount: cross ? parseMoney(f.dest) : null,
      original_amount: origAllowed && orig > 0 ? orig : null, original_currency: origAllowed && orig > 0 ? f.origCur : null,
    };
    setSaving(true);
    try {
      const r = initial ? await api.put<{ warning?: string | null }>(`/transactions/${initial.id}`, payload) : await api.post<{ warning?: string | null }>("/transactions", payload);
      toast.success(initial ? "Movimiento actualizado" : "Movimiento guardado");
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="transaction-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">{initial ? "Editar movimiento" : "Nuevo movimiento"}</DialogTitle>
          <DialogDescription>
            {loanPayment ? "El pago del préstamo reduce la deuda y cuenta como gasto en la categoría que elijas." : "Pagar la tarjeta o apartar para ahorro es un traspaso, no un gasto."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="transaction-form">
          <div className={`grid ${types.length === 4 ? "grid-cols-4" : "grid-cols-3"} gap-1 rounded-lg bg-[#F4F5F7] p-1`} role="tablist">
            {types.map((t) => (
              <button key={t} type="button" onClick={() => changeType(t)} data-testid={`tx-type-${t}`}
                className={`rounded-md py-2 text-xs font-semibold transition-colors sm:text-sm ${f.type === t ? "bg-[#1B2A4A] text-white" : "text-slate-600 hover:text-[#1B2A4A]"}`}>
                {TX_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          {isTransfer && (
            <Pick label="Tipo de traspaso" value={f.kind} onChange={(v) => setKind(v as TransferKind)} testId="tx-transfer-kind" placeholder="Tipo de traspaso"
              options={TRANSFER_KIND_ORDER.map(({ kind, label }) => ({ value: kind, label }))} />
          )}
          <div className="space-y-1.5">
            <Label htmlFor="tx-amount" className="text-sm text-slate-700">{cross ? `Monto que sale (${srcCur})` : `Monto${amountCur !== "MXN" ? ` (${amountCur})` : ""}`}</Label>
            <div className="flex gap-2">
              {f.type === "adjustment" && (
                <div className="flex shrink-0 rounded-md bg-[#F4F5F7] p-1">
                  {["+", "-"].map((s) => (
                    <button key={s} type="button" onClick={() => set("sign")(s)} data-testid={`tx-sign-${s === "+" ? "plus" : "minus"}`}
                      className={`w-10 rounded text-lg font-bold ${f.sign === s ? "bg-[#1B2A4A] text-white" : "text-slate-600"}`}>{s === "+" ? "+" : "−"}</button>
                  ))}
                </div>
              )}
              <div className="flex-1">
                <MoneyInput id="tx-amount" autoFocus value={f.amount} onChange={set("amount")} className="h-12 text-lg font-semibold" testId="tx-amount-input" />
              </div>
            </div>
          </div>
          {isExpense && (
            <Pick label="Categoría" value={category} onChange={pickCategory} testId="tx-category-select" placeholder="Elige la categoría" options={catOptions} />
          )}
          {usesConcept ? (
            <ConceptInput value={f.description} items={expenseItems} selectedId={matched?.id ?? null} onText={typeConcept} onPick={pickConcept} showTag={isExpense} />
          ) : isTransfer ? null : (
          <div className="space-y-1.5">
            <Label htmlFor="tx-description" className="text-sm text-slate-700">Descripción{f.type === "adjustment" ? " (opcional)" : ""}</Label>
            <Input id="tx-description" value={f.description} maxLength={140}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("description")(e.target.value)} className="h-11" data-testid="tx-description-input" />
          </div>
          )}
          {matched && isExpense && (
            <p className="-mt-2 text-sm text-slate-500" data-testid="tx-matched-info">
              Del presupuesto: <b className="text-[#1B2A4A]">{matched.category_name}</b>
            </p>
          )}
          {isExpense && (
            <Pick label="Tipo" value={group} onChange={set("group")} testId="tx-group-select" placeholder="Esencial, Comodidad o Deuda" options={GROUP_OPTS} />
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {needsSource && <Pick label={f.type === "transfer" ? "Sale de" : "¿Con qué pagaste?"} value={f.source} onChange={(v) => setF((s) => ({ ...s, source: v, msi: NONE }))} options={isTransfer ? transferSrc : expenseSrcOptions} testId="tx-source-select" placeholder="Cuenta" onCreate={newSource} />}
            {needsDest && <Pick label={f.type === "income" ? "¿A qué cuenta entró?" : f.type === "transfer" ? "Entra a" : "Cuenta a ajustar"} value={f.destination} onChange={setDest} options={f.type === "income" ? incomeOptions : isTransfer ? transferDest : accOptions} testId="tx-destination-select" placeholder="Cuenta" onCreate={newDest} />}
            {msiAllowed && <Pick label="¿A meses sin intereses?" value={f.msi} onChange={set("msi")} options={MSI_OPTS} testId="tx-msi-select" placeholder="No" />}
            {showCategory && !usesConcept && (
              <Pick label={savingsTransfer ? "Concepto de Ahorro e Inversión" : f.type === "income" ? "Concepto del presupuesto (opcional)" : "Gasto del presupuesto (opcional)"} value={item} onChange={pickItem} testId="tx-budget-item-select" placeholder={savingsTransfer ? "Concepto" : "Gasto del presupuesto"}
                options={[{ value: NONE, label: savingsTransfer ? "No presupuestado" : "Ninguno" }, ...itemOptions]} />
            )}
            {showCatPicker && !isExpense && (
              <Pick label={categoryRequired ? "Categoría" : "Categoría (opcional)"} value={category} onChange={isExpense ? pickCategory : (v) => setF((s) => ({ ...s, category: v, item: NONE }))} testId="tx-category-select" placeholder="Categoría"
                options={[...(categoryRequired ? [] : [{ value: NONE, label: "Sin categoría" }]), ...catOptions]} />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="tx-date" className="text-sm text-slate-700">Fecha</Label>
              <DateInput id="tx-date" value={f.date} onChange={set("date")} testId="tx-date-input" calendar />
            </div>
          </div>
          <FxFields s={f as FxState} set={(k) => set(k)} amount={f.amount} date={f.date} srcCur={srcCur} dstCur={dstCur} cross={cross} foreign={foreign} origAllowed={origAllowed} />
          <div className="space-y-1.5">
            <Label htmlFor="tx-notes" className="text-sm text-slate-700">{f.type === "adjustment" && !investReturn ? "Motivo (obligatorio)" : "Notas (opcional)"}</Label>
            <Textarea id="tx-notes" rows={2} value={f.notes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set("notes")(e.target.value)} data-testid="tx-notes-input" />
          </div>
          <Button type="submit" disabled={saving} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="tx-submit-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : initial ? "Guardar cambios" : "Guardar movimiento"}
          </Button>
        </form>
      </DialogContent>
      {dialog}
    </Dialog>
  );
}
